import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  Users,
  CreditCard,
  Video,
  Building2,
  Plus,
  ArrowUpRight,
  UserPlus,
  Coins,
  Trash2,
  Copy,
  Check,
  AlertCircle,
  RefreshCw,
  Upload,
  Download,
  FileSpreadsheet,
  MapPin,
  Wallet,
  History,
  Send,
  Calendar,
  CheckCircle2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import BuyCreditsModal from '../../components/BuyCreditsModal';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OrganizationDashboard({ initialTab = 'overview' }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [branding, setBranding] = useState({ logo_url: '', org_slug: '', brand_color: '#84cc16' });
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [showAllocateCredits, setShowAllocateCredits] = useState(false);
  const [showImportCSV, setShowImportCSV] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [createdAgentPassword, setCreatedAgentPassword] = useState(null);
  const [createdAgentEmail, setCreatedAgentEmail] = useState(null);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [mainSection, setMainSection] = useState(initialTab);
  
  // Form states
  const [newAgent, setNewAgent] = useState({ name: '', email: '', phone: '' });
  const [allocateCredits, setAllocateCredits] = useState({ credits: 0, message: '' });
  const [submitting, setSubmitting] = useState(false);
  
  // CSV import states
  const [csvFile, setCsvFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Credit history state
  const [creditHistory, setCreditHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Buy credits modal
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const [dashRes, brandRes] = await Promise.all([
        axios.get(`${API}/api/organization/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/organization/branding`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: {} })) // Fallback if branding not set
      ]);
      setDashboardData(dashRes.data);
      setBranding({
        logo_url: brandRes.data.logo_url || '',
        org_slug: brandRes.data.org_slug || '',
        brand_color: brandRes.data.brand_color || '#84cc16'
      });
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreditHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await axios.get(`${API}/api/organization/credit-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditHistory(response.data.history || []);
    } catch (error) {
      console.error('Error fetching credit history:', error);
      toast.error('Failed to load credit history');
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchDashboard();
    }
  }, [token]);

  useEffect(() => {
    setMainSection(initialTab);
  }, [initialTab]);

  // Fetch credit history when billing tab is opened
  useEffect(() => {
    if (mainSection === 'billing' && token && creditHistory.length === 0) {
      fetchCreditHistory();
    }
  }, [mainSection, token]);

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    if (!newAgent.name || !newAgent.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await axios.post(`${API}/api/organization/agents`, newAgent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Agent created successfully!');
      setCreatedAgentPassword(response.data.temp_password);
      setCreatedAgentEmail(newAgent.email);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create agent');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAllocateCredits = async (e) => {
    e.preventDefault();
    if (allocateCredits.credits <= 0) {
      toast.error('Please enter a valid credit amount');
      return;
    }
    
    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API}/api/organization/agents/${selectedAgent.id}/allocate-credits`,
        {
          agent_id: selectedAgent.id,
          credits: allocateCredits.credits,
          message: allocateCredits.message || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      setShowAllocateCredits(false);
      setAllocateCredits({ credits: 0, message: '' });
      setSelectedAgent(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allocate credits');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAgent = async (agent) => {
    if (!window.confirm(`Are you sure you want to remove ${agent.name} from your organization? Any unspent allocated credits will be returned.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/api/organization/agents/${agent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove agent');
    }
  };

  const handleCSVImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', csvFile);
      
      const response = await axios.post(`${API}/api/organization/agents/import-csv`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResults(response.data.results);
      toast.success(response.data.message);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    const csvContent = "name,email,quota\nJohn Doe,john@example.com,10\nJane Smith,jane@example.com,20";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agent_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openAllocateDialog = (agent) => {
    setSelectedAgent(agent);
    setAllocateCredits({ credits: 0, message: '' });
    setShowAllocateCredits(true);
  };

  const resetCreateDialog = () => {
    setShowCreateAgent(false);
    setCreatedAgentPassword(null);
    setCreatedAgentEmail(null);
    setNewAgent({ name: '', email: '', phone: '' });
  };

  const resetImportDialog = () => {
    setShowImportCSV(false);
    setCsvFile(null);
    setImportResults(null);
  };

  // All agents (no regional office filter)
  const filteredAgents = dashboardData?.agents || [];

  if (loading) {
    return (
      <DashboardLayout role="organization">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="organization">
      <div className="space-y-6">
        {/* Welcome with Logo */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Organization Logo */}
            {branding.logo_url ? (
              <div className="w-16 h-16 rounded-lg border bg-white p-2 flex items-center justify-center flex-shrink-0">
                <img 
                  src={branding.logo_url} 
                  alt="Organization Logo" 
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : (
              <div 
                className="w-16 h-16 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: branding.brand_color || '#84cc16' }}
              >
                <Building2 className="w-8 h-8 text-white" />
              </div>
            )}
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl">Welcome, {user?.organization_name || user?.name}</h2>
              <p className="text-muted-foreground">Manage your organization and agents</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline"
              onClick={() => setShowImportCSV(true)}
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                resetCreateDialog();
                setShowCreateAgent(true);
              }}
              data-testid="add-agent-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Add Agent
            </Button>
            <Button 
              className="bg-lime-500 hover:bg-lime-600 text-white"
              onClick={() => setShowBuyCredits(true)}
              data-testid="buy-credits-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Buy Credits
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
          {/* Total Credits Card - Sum of Personal + Distributed */}
          <Card className="border-lime-500/30 bg-gradient-to-br from-lime-500/5 to-lime-600/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Credits</p>
                  <p className="text-2xl font-heading font-bold text-lime-600">
                    {(dashboardData?.credits || 0) + (dashboardData?.distributed_credits || 0)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-lime-500/20 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-lime-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Personal Credits</p>
                  <p className="text-2xl font-heading font-bold text-lime-500">{dashboardData?.credits || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-lime-500/10 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-lime-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Distributed</p>
                  <p className="text-2xl font-heading font-bold text-amber-500">{dashboardData?.distributed_credits || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                  <Coins className="w-5 h-5 text-amber-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-red-500/30">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Consumed</p>
                  <p className="text-2xl font-heading font-bold text-red-500">{dashboardData?.consumed_credits || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Total Agents</p>
                  <p className="text-2xl font-heading font-bold text-blue-500">{dashboardData?.total_agents || 0}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Interviews</p>
                  <p className="text-2xl font-heading font-bold">{dashboardData?.total_interviews || 0}</p>
                  <p className="text-xs text-muted-foreground">{dashboardData?.pass_rate || 0}% pass</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Video className="w-5 h-5 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Section Navigation */}
        <div className="border-b">
          <nav className="flex gap-4">
            <button
              onClick={() => setMainSection('overview')}
              className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                mainSection === 'overview' 
                  ? 'border-lime-500 text-lime-500' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setMainSection('team')}
              className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                mainSection === 'team' 
                  ? 'border-lime-500 text-lime-500' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Team Members
            </button>
            <button
              onClick={() => setMainSection('interviews')}
              className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                mainSection === 'interviews' 
                  ? 'border-lime-500 text-lime-500' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Interviews
            </button>
            <button
              onClick={() => setMainSection('billing')}
              className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                mainSection === 'billing' 
                  ? 'border-lime-500 text-lime-500' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Credits & Billing
            </button>
          </nav>
        </div>

        {/* Overview & Team Section */}
        {(mainSection === 'overview' || mainSection === 'team') && (
          <>
        {/* Agents Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <div>
                <CardTitle className="font-heading">Agents</CardTitle>
                <CardDescription>Manage your agents network</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchDashboard}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredAgents.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Personal</TableHead>
                    <TableHead>Allocated</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAgents.map((agent) => (
                    <TableRow key={agent.id} data-testid={`agent-row-${agent.id}`}>
                      <TableCell className="font-medium">{agent.name}</TableCell>
                      <TableCell>{agent.email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-lime-500">
                          {agent.personal_credits || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-amber-500/10 text-amber-500">
                          {agent.allocated_credits || 0}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>
                          {agent.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAllocateDialog(agent)}
                            disabled={dashboardData?.credits === 0}
                          >
                            <Coins className="w-4 h-4 mr-1" />
                            Allocate
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => handleRemoveAgent(agent)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No team members yet</p>
                <p className="text-sm mt-2">
                  Create regional offices and agents to help manage interviews.
                </p>
                <div className="flex gap-2 justify-center mt-4">
                  <Button onClick={() => setShowImportCSV(true)} variant="outline">
                    <Upload className="w-4 h-4 mr-2" />
                    Import CSV
                  </Button>
                  <Button onClick={() => { resetCreateDialog(); setShowCreateAgent(true); }}>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Manually
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}

        {/* Interviews Section */}
        {mainSection === 'interviews' && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Organization Interviews</CardTitle>
              <CardDescription>View all interviews from your agents and regional offices</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 text-muted-foreground">
                <Video className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">Interview visibility coming soon</p>
                <p className="text-sm mt-2">You'll be able to see all interviews conducted by your team here.</p>
                <div className="mt-4 p-4 bg-muted/50 rounded-lg max-w-md mx-auto">
                  <p className="text-sm"><strong>Stats:</strong></p>
                  <p className="text-sm">Total Interviews: {dashboardData?.total_interviews || 0}</p>
                  <p className="text-sm">Pass Rate: {dashboardData?.pass_rate || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Billing Section */}
        {mainSection === 'billing' && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Credits & Billing</CardTitle>
                <CardDescription>Manage your credits and purchase more</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-6">
                    <p className="text-sm text-muted-foreground">Available Credits</p>
                    <p className="text-4xl font-heading font-bold text-lime-500">{dashboardData?.credits || 0}</p>
                    <p className="text-sm text-muted-foreground mt-2">Use for any applications</p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6">
                    <p className="text-sm text-muted-foreground">Credits Distributed</p>
                    <p className="text-4xl font-heading font-bold text-blue-500">{dashboardData?.distributed_credits || 0}</p>
                    <p className="text-sm text-muted-foreground mt-2">Allocated to team members</p>
                  </div>
                </div>
                <Button 
                  className="w-full bg-lime-500 hover:bg-lime-600 h-12"
                  onClick={() => setShowBuyCredits(true)}
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Buy More Credits
                </Button>
              </CardContent>
            </Card>

            {/* Credit Distribution History */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <History className="w-5 h-5 text-lime-500" />
                      Credit Distribution History
                    </CardTitle>
                    <CardDescription>Track all credits distributed to your team members</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchCreditHistory} disabled={loadingHistory}>
                    <RefreshCw className={`w-4 h-4 mr-2 ${loadingHistory ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-muted-foreground">Loading history...</span>
                  </div>
                ) : creditHistory.length > 0 ? (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Recipient</TableHead>
                          <TableHead className="text-center">Credits</TableHead>
                          <TableHead>Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {creditHistory.map((transaction, idx) => (
                          <TableRow key={transaction.id || idx}>
                            <TableCell className="whitespace-nowrap">
                              <div className="flex items-center gap-2 text-sm">
                                <Calendar className="w-4 h-4 text-muted-foreground" />
                                {new Date(transaction.created_at).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {new Date(transaction.created_at).toLocaleTimeString('en-US', {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-lime-500/10 flex items-center justify-center">
                                  <Send className="w-4 h-4 text-lime-500" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm">{transaction.to_agent_name}</p>
                                  <p className="text-xs text-muted-foreground">{transaction.to_agent_email}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className="bg-lime-500/10 text-lime-600 border-lime-500/20">
                                <Coins className="w-3 h-3 mr-1" />
                                {transaction.credits}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[200px]">
                              <p className="text-sm text-muted-foreground truncate">
                                {transaction.notes || '-'}
                              </p>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <History className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No credit distributions yet</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Credits distributed to agents will appear here
                    </p>
                  </div>
                )}

                {creditHistory.length > 0 && (
                  <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-lime-500">{creditHistory.length}</p>
                        <p className="text-xs text-muted-foreground">Total Transactions</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-500">
                          {creditHistory.reduce((sum, t) => sum + (t.credits || 0), 0)}
                        </p>
                        <p className="text-xs text-muted-foreground">Credits Distributed</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-purple-500">
                          {new Set(creditHistory.map(t => t.to_agent_id)).size}
                        </p>
                        <p className="text-xs text-muted-foreground">Unique Recipients</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Actions - Only on Overview */}
        {mainSection === 'overview' && (
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="cursor-pointer hover:border-lime-500/50 transition-colors" onClick={() => setShowBuyCredits(true)}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold">Buy More Credits</h3>
                  <p className="text-sm text-muted-foreground">Get bulk credits at organization rates</p>
                </div>
                <ArrowUpRight className="w-5 h-5 text-lime-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:border-lime-500/50 transition-colors" onClick={() => setShowImportCSV(true)}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold">Bulk Import Agents</h3>
                  <p className="text-sm text-muted-foreground">Import multiple agents via CSV</p>
                </div>
                <ArrowUpRight className="w-5 h-5 text-lime-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="cursor-pointer hover:border-lime-500/50 transition-colors" onClick={() => { resetCreateDialog(); setShowCreateAgent(true); }}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold">Add Regional Office</h3>
                  <p className="text-sm text-muted-foreground">Create a regional office to manage sub-agents</p>
                </div>
                <ArrowUpRight className="w-5 h-5 text-lime-500" />
              </div>
            </CardContent>
          </Card>
        </div>
        )}
      </div>

      {/* Create Agent Dialog */}
      <Dialog open={showCreateAgent} onOpenChange={setShowCreateAgent}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Team Member</DialogTitle>
            <DialogDescription>
              Add an agent or regional office to your organization.
            </DialogDescription>
          </DialogHeader>
          
          {createdAgentPassword ? (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-green-700 dark:text-green-400">
                    {newAgent.is_regional_office ? 'Regional Office' : 'Agent'} Created!
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Share these credentials (also sent via email):
                </p>
                <div className="bg-white dark:bg-gray-800 rounded border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Email:</span>
                    <span className="font-mono text-sm">{createdAgentEmail}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Password:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{createdAgentPassword}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0"
                        onClick={() => copyToClipboard(createdAgentPassword)}
                      >
                        {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Type:</span>
                    <Badge className={newAgent.is_regional_office ? "bg-purple-500/10 text-purple-500" : ""}>
                      {newAgent.is_regional_office ? 'Regional Office' : 'Agent'}
                    </Badge>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={resetCreateDialog}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleCreateAgent} className="space-y-4">
              <div className="flex items-center space-x-2 p-3 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <Checkbox
                  id="is_regional_office"
                  checked={newAgent.is_regional_office}
                  onCheckedChange={(checked) => setNewAgent({ ...newAgent, is_regional_office: checked })}
                />
                <div className="flex-1">
                  <Label htmlFor="is_regional_office" className="text-sm font-medium cursor-pointer">
                    Mark as Regional Office
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Regional offices can create sub-agents and distribute credits
                  </p>
                </div>
                <MapPin className="w-5 h-5 text-purple-500" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="agent-name">Full Name *</Label>
                <Input
                  id="agent-name"
                  placeholder="John Doe"
                  value={newAgent.name}
                  onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="agent-email">Email Address *</Label>
                <Input
                  id="agent-email"
                  type="email"
                  placeholder="john@example.com"
                  value={newAgent.email}
                  onChange={(e) => setNewAgent({ ...newAgent, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="agent-phone">Phone Number</Label>
                <Input
                  id="agent-phone"
                  placeholder="+1 234 567 8900"
                  value={newAgent.phone}
                  onChange={(e) => setNewAgent({ ...newAgent, phone: e.target.value })}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetCreateDialog}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Create {newAgent.is_regional_office ? 'Regional Office' : 'Agent'}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Allocate Credits Dialog */}
      <Dialog open={showAllocateCredits} onOpenChange={setShowAllocateCredits}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Allocate Credits</DialogTitle>
            <DialogDescription>
              Allocate credits to {selectedAgent?.name}. 
              {selectedAgent?.is_regional_office 
                ? " Regional offices can further distribute these to their sub-agents."
                : " These credits can only be used for your organization's interviews."}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleAllocateCredits} className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Your Available Credits:</span>
                <span className="font-medium text-lime-500">{dashboardData?.credits || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Their Current Allocated:</span>
                <span className="font-medium">{selectedAgent?.allocated_credits || 0}</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="credits-amount">Credits to Allocate *</Label>
              <Input
                id="credits-amount"
                type="number"
                min="1"
                max={dashboardData?.credits || 0}
                placeholder="Enter amount"
                value={allocateCredits.credits || ''}
                onChange={(e) => setAllocateCredits({ ...allocateCredits, credits: parseInt(e.target.value) || 0 })}
                required
              />
              <div className="flex gap-2 mt-2">
                {[1, 5, 10, 25, 50].map((amount) => (
                  <Button
                    key={amount}
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={amount > (dashboardData?.credits || 0)}
                    onClick={() => setAllocateCredits({ ...allocateCredits, credits: amount })}
                  >
                    +{amount}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="credits-message">Message (Optional)</Label>
              <Textarea
                id="credits-message"
                placeholder="Add a note..."
                value={allocateCredits.message}
                onChange={(e) => setAllocateCredits({ ...allocateCredits, message: e.target.value })}
                rows={2}
              />
            </div>

            {allocateCredits.credits > 0 && (
              <div className="bg-lime-50 dark:bg-lime-900/20 border border-lime-200 dark:border-lime-800 rounded-lg p-3">
                <p className="text-sm">
                  After allocation: <strong className="text-lime-600">{(dashboardData?.credits || 0) - allocateCredits.credits}</strong> credits remaining
                </p>
              </div>
            )}
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAllocateCredits(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting || allocateCredits.credits <= 0 || allocateCredits.credits > (dashboardData?.credits || 0)}
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Allocating...
                  </>
                ) : (
                  <>
                    <Coins className="w-4 h-4 mr-2" />
                    Allocate {allocateCredits.credits} Credits
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={showImportCSV} onOpenChange={setShowImportCSV}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-lime-500" />
              Bulk Import Agents
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file to add agents. Existing agents will be linked and receive credits.
            </DialogDescription>
          </DialogHeader>
          
          {importResults ? (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Check className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-green-700 dark:text-green-400">
                    Import Complete!
                  </span>
                </div>
                <p className="text-sm space-x-2">
                  {(importResults.created?.length > 0) && (
                    <span className="text-green-600">{importResults.created.length} new agents created</span>
                  )}
                  {(importResults.linked?.length > 0) && (
                    <span className="text-blue-600">{importResults.linked.length} existing agents linked</span>
                  )}
                  {importResults.failed?.length > 0 && (
                    <span className="text-red-600">({importResults.failed.length} failed)</span>
                  )}
                </p>
                {importResults.total_credits_allocated > 0 && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Total credits allocated: {importResults.total_credits_allocated}
                  </p>
                )}
              </div>
              
              {/* New Agents Created */}
              {importResults.created?.length > 0 && (
                <div className="max-h-36 overflow-y-auto">
                  <p className="text-sm font-medium mb-2 text-green-600">✓ New Agents Created:</p>
                  <div className="space-y-2">
                    {importResults.created.map((agent, idx) => (
                      <div key={idx} className="text-sm p-2 bg-green-50 dark:bg-green-900/20 rounded flex justify-between items-center">
                        <div>
                          <span className="font-medium">{agent.name}</span>
                          <span className="text-muted-foreground ml-2">({agent.email})</span>
                        </div>
                        <div className="text-xs">
                          <span className="text-muted-foreground">Pass: </span>
                          <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{agent.temp_password}</code>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Existing Agents Linked */}
              {importResults.linked?.length > 0 && (
                <div className="max-h-36 overflow-y-auto">
                  <p className="text-sm font-medium mb-2 text-blue-600">↗ Existing Agents Linked:</p>
                  <div className="space-y-2">
                    {importResults.linked.map((agent, idx) => (
                      <div key={idx} className="text-sm p-2 bg-blue-50 dark:bg-blue-900/20 rounded flex justify-between items-center">
                        <div>
                          <span className="font-medium">{agent.name}</span>
                          <span className="text-muted-foreground ml-2">({agent.email})</span>
                        </div>
                        <span className="text-blue-600 text-xs">+{agent.quota} credits sent</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {importResults.failed?.length > 0 && (
                <div className="max-h-32 overflow-y-auto">
                  <p className="text-sm font-medium mb-2 text-red-600">✗ Failed:</p>
                  <div className="space-y-1">
                    {importResults.failed.map((item, idx) => (
                      <div key={idx} className="text-sm text-red-600 p-2 bg-red-50 dark:bg-red-900/20 rounded">
                        {item.email}: {item.error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <DialogFooter>
                <Button onClick={resetImportDialog}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={(e) => setCsvFile(e.target.files[0])}
                  className="hidden"
                />
                {csvFile ? (
                  <div className="space-y-2">
                    <FileSpreadsheet className="w-12 h-12 mx-auto text-lime-500" />
                    <p className="font-medium">{csvFile.name}</p>
                    <Button variant="outline" size="sm" onClick={() => setCsvFile(null)}>
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="w-12 h-12 mx-auto text-muted-foreground" />
                    <p className="text-muted-foreground">Drop your CSV file here or</p>
                    <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                      Browse Files
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">CSV Format:</p>
                <code className="text-xs block bg-gray-100 dark:bg-gray-800 p-2 rounded">
                  name,email,quota,is_regional_office<br/>
                  John Doe,john@example.com,10,no<br/>
                  Jane Smith,jane@example.com,20,yes
                </code>
                <Button variant="link" size="sm" className="mt-2 p-0 h-auto" onClick={downloadTemplate}>
                  <Download className="w-4 h-4 mr-1" />
                  Download Template
                </Button>
              </div>

              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-700 dark:text-amber-400">Note:</p>
                    <p className="text-muted-foreground">
                      Credits will be deducted from your balance. Agents will receive an email with login credentials.
                    </p>
                  </div>
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={resetImportDialog}>
                  Cancel
                </Button>
                <Button onClick={handleCSVImport} disabled={!csvFile || importing}>
                  {importing ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Import Agents
                    </>
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Buy Credits Modal */}
      <BuyCreditsModal 
        open={showBuyCredits} 
        onOpenChange={setShowBuyCredits}
        onSuccess={() => {
          fetchDashboard();
          toast.success('Credits purchased successfully!');
        }}
      />
    </DashboardLayout>
  );
}
